import { P } from "../theme";
import { sr } from "./rng";

export function Constellation({
  seed = 42,
  threads: threadCount = 4,
  accent,
  luminous = false,
  glowEdges = 0,
  avoidCenter = false,
  style = {},
}) {
  const rng = sr(seed);
  const base = accent || "#9590A8";
  const pops = luminous ? [base, "#88D0DC", "#E4DED4", "#A8A098"] : P.nodes;

  const isInCenter = (x, y) => Math.abs(x - 50) < 22 && Math.abs(y - 50) < 22;
  const pushOut = (x, y) => {
    if (!avoidCenter || !isInCenter(x, y)) return { x, y };
    const dx = x - 50, dy = y - 50;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const targetDist = 25 + rng() * 10;
    return { x: 50 + (dx / dist) * targetDist, y: 50 + (dy / dist) * targetDist };
  };

  const nodes = [];
  const edges = [];
  const polygons = [];

  const clusterCount = 3 + Math.floor(threadCount * 0.5);
  const gridCols = Math.ceil(Math.sqrt(clusterCount * 1.5));
  const gridRows = Math.ceil(clusterCount / gridCols);
  const clusters = [];

  for (let ci = 0; ci < clusterCount; ci++) {
    const gr = Math.floor(ci / gridCols);
    const gc = ci % gridCols;
    let cx = ((gc + 0.5) / gridCols) * 120 - 10 + (rng() - 0.5) * (80 / gridCols);
    let cy = ((gr + 0.5) / gridRows) * 120 - 10 + (rng() - 0.5) * (80 / gridRows);
    const pushed = pushOut(cx, cy);
    cx = pushed.x; cy = pushed.y;
    const sides = 3 + Math.floor(rng() * 4);
    const radius = 10 + rng() * 16;
    const rotation = rng() * Math.PI * 2;
    const clusterColor = rng() > 0.4 ? pops[Math.floor(rng() * pops.length)] : base;
    const startIdx = nodes.length;

    for (let s = 0; s < sides; s++) {
      const angle = rotation + (s / sides) * Math.PI * 2;
      const r2 = radius * (0.85 + rng() * 0.3);
      const baseOp = luminous ? 0.35 + rng() * 0.2 : 0.22 + rng() * 0.16;
      nodes.push({
        x: cx + Math.cos(angle) * r2,
        y: cy + Math.sin(angle) * r2,
        sz: 0.6 + rng() * 0.8,
        op: baseOp,
        color: rng() > 0.55 ? pops[Math.floor(rng() * pops.length)] : clusterColor,
        pulse: rng() > (luminous ? 0.5 : 0.7),
        cluster: ci,
      });
    }

    for (let a = startIdx; a < nodes.length; a++) {
      for (let b = a + 1; b < nodes.length; b++) {
        const ai = a - startIdx, bi = b - startIdx;
        const isPerimeter = bi === (ai + 1) % sides || ai === (bi + 1) % sides;
        if (isPerimeter || rng() > 0.4) {
          const edgeOp = luminous
            ? (isPerimeter ? 0.24 + rng() * 0.14 : 0.12 + rng() * 0.1)
            : (isPerimeter ? 0.14 + rng() * 0.1 : 0.07 + rng() * 0.07);
          edges.push({ a, b, op: edgeOp, color: clusterColor });
        }
      }
    }

    if (sides >= 4 && rng() > 0.25) {
      const centerIdx = nodes.length;
      nodes.push({
        x: cx + (rng() - 0.5) * 3,
        y: cy + (rng() - 0.5) * 3,
        sz: 0.9 + rng() * 0.7,
        op: 0.25 + rng() * 0.12,
        color: pops[Math.floor(rng() * pops.length)],
        pulse: rng() > 0.5,
        cluster: ci,
      });
      for (let a = startIdx; a < centerIdx; a++) {
        if (rng() > 0.3) {
          edges.push({ a, b: centerIdx, op: 0.1 + rng() * 0.07, color: clusterColor });
        }
      }
    }

    if (sides >= 3 && rng() > 0.3) {
      const i0 = startIdx;
      const i1 = startIdx + 1;
      const i2 = startIdx + Math.min(2, sides - 1);
      polygons.push({
        points: `${nodes[i0].x},${nodes[i0].y} ${nodes[i1].x},${nodes[i1].y} ${nodes[i2].x},${nodes[i2].y}`,
        color: clusterColor,
        op: luminous ? 0.05 + rng() * 0.05 : 0.035 + rng() * 0.035,
      });
    }

    clusters.push({ cx, cy, startIdx, endIdx: nodes.length, color: clusterColor });
  }

  const scatterCount = 3 + Math.floor(threadCount * 0.6);
  for (let i = 0; i < scatterCount; i++) {
    let sx = rng() * 100, sy = rng() * 100;
    const sp = pushOut(sx, sy);
    sx = sp.x; sy = sp.y;
    nodes.push({
      x: sx, y: sy,
      sz: 0.3 + rng() * 0.5,
      op: 0.1 + rng() * 0.12,
      color: rng() > 0.5 ? pops[Math.floor(rng() * pops.length)] : base,
      pulse: false,
      cluster: -1,
    });
  }

  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      if (nodes[i].cluster === nodes[j].cluster && nodes[i].cluster !== -1) continue;
      const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
      if (d < 26 && d > 5 && rng() > 0.55) {
        edges.push({ a: i, b: j, op: 0.06 + rng() * 0.06, color: base });
      }
    }
  }

  const glowSet = [];
  if (glowEdges > 0 && edges.length > 0) {
    const rng2 = sr(seed + 999);
    const pool = edges.filter(e => {
      const d = Math.hypot(nodes[e.a].x - nodes[e.b].x, nodes[e.a].y - nodes[e.b].y);
      return d > 8;
    });
    const count = Math.min(glowEdges, pool.length);
    const shuffled = pool.sort(() => rng2() - 0.5);
    for (let i = 0; i < count; i++) {
      const e = shuffled[i];
      const dur = 8 + rng2() * 12;
      const delay = rng2() * 20;
      glowSet.push({ ...e, dur, delay });
    }
  }

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        ...style,
      }}
    >
      <defs>
        <style>{`@keyframes twinkle{0%,100%{opacity:var(--base)}50%{opacity:calc(var(--base) + ${luminous ? "0.2" : "0.12"})}}`}</style>
      </defs>
      {polygons.map((p, i) => (
        <polygon key={`pg${i}`} points={p.points} fill={p.color} opacity={p.op} />
      ))}
      {edges.map((e, i) => (
        <line
          key={`e${i}`}
          x1={nodes[e.a].x} y1={nodes[e.a].y}
          x2={nodes[e.b].x} y2={nodes[e.b].y}
          stroke={e.color} strokeWidth="0.15" opacity={e.op}
        />
      ))}
      {glowSet.map((g, i) => {
        const ax = nodes[g.a].x, ay = nodes[g.a].y;
        const bx = nodes[g.b].x, by = nodes[g.b].y;
        const dx = bx - ax, dy = by - ay;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        const tx = (dx / len) * 3, ty = (dy / len) * 3;
        return (
          <line
            key={`glow-${i}`}
            x1={-tx / 2} y1={-ty / 2}
            x2={tx / 2} y2={ty / 2}
            stroke="#E4DED4" strokeWidth="0.3" strokeLinecap="round" opacity="0"
          >
            <animateMotion
              dur={`${g.dur}s`} begin={`${g.delay}s`} repeatCount="indefinite"
              calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1"
              keyTimes="0;0.5;1" keyPoints="0;1;0"
              path={`M${ax},${ay} L${bx},${by}`}
            />
            <animate
              attributeName="opacity" values="0;0.45;0.45;0"
              keyTimes="0;0.08;0.92;1"
              dur={`${g.dur}s`} begin={`${g.delay}s`} repeatCount="indefinite"
            />
          </line>
        );
      })}
      {nodes.map((n, i) => (
        <circle
          key={`n${i}`} cx={n.x} cy={n.y} r={n.sz} fill={n.color} opacity={n.op}
          style={n.pulse ? { "--base": n.op, animation: `twinkle ${5 + (i % 4)}s ease-in-out infinite` } : undefined}
        />
      ))}
    </svg>
  );
}
